import React, { useContext, useEffect, useMemo, useState } from "react";
import style from "./index.module.scss";
import FileList from "../../../FileList";
import { Button, message, Spin } from "antd";
import Footer from "../Footer";
import {
    ActivityTaskDetail,
    queryGroupActivityDetail,
    publishGroupActivity,
    GroupType,
    queryRandomGroup,
    GroupListItem
} from "service/group";
import { errorMessage } from "service/service";
import { ResultMessage } from "components/ResultMessage";
import { GroupContext, PreviewAtom } from "../../../../store";
import TaskListMenu from "../TaskListMenu";
import { useToggle } from "react-use";
import AsideLink from "./Components/AsideLink";
import PreviewFileModal from "components/PreviewFileModal";
import _ from "underscore";
import useRequest from "utils/useRequest";
import { useAtomValue } from "jotai";
import CustomEmpty from "components/CourseComponents/CustomEmpty";
import SubmitError from "components/SubmitError";
import useRequestFn from "../../../../../../../utils/useRequestFn";

interface StepThreeProps {
    activityId: number;
    onChangeStep: (val: number) => void;
    onCancel: () => void;
    onOk: () => void; //确认
}

export default function StepThree({
    activityId,
    onChangeStep,
    onCancel,
    onOk
}: StepThreeProps) {
    const { groupList } = useContext(GroupContext);
    const [taskId, setTaskId] = useState(-1); //任务id
    const [taskDetail, setTaskDetail] = useState<
        ActivityTaskDetail | undefined
    >();
    const preview = useAtomValue(PreviewAtom);

    //任务列表
    const [data, { loading }] = useRequest(
        async () => {
            if (activityId > 0) {
                return queryGroupActivityDetail(activityId);
            } else {
                return null;
            }
        },
        {},
        [activityId]
    );

    //获取当前选中的任务id，任务详情
    useEffect(() => {
        if (data?.taskList && data.taskList.length !== 0) {
            setTaskId(data.taskList[0].id);
            setTaskDetail(data.taskList[0]);
        }
    }, [data]);

    useEffect(() => {
        const detail = data?.taskList.find(item => item.id === taskId);
        setTaskDetail(detail);
    }, [taskId]);

    //下载文件
    const handelDownload = (fileName: string, url: string) => {
        const newUrl = `${url}?attname=${encodeURIComponent(fileName)}`;
        window.open(newUrl);
    };

    //预览
    const [defaultIndex, setDefaultIndex] = useState(0);

    function onPrev(id?: number) {
        togglePrev();
        const index = _.findIndex(
            taskDetail?.taskFileEntityList || [],
            item => item.id === id
        );

        if (index !== undefined && index !== -1) {
            setDefaultIndex(index);
        }
    }

    //预览弹窗
    const [prevShow, togglePrev] = useToggle(false);

    //发布
    const [onSubmit, { loading: submitLoading }] =
        useRequestFn(publishGroupActivity);
    const onPublish = async () => {
        try {
            await onSubmit(activityId);
            await onOk();
            message.success("发布成功");
            onCancel();
        } catch (e) {
            ResultMessage.error(
                <SubmitError title="发布失败" content={errorMessage(e)} />,
                { duration: 2000, width: 300 }
            );
        }
    };

    //小组列表
    const [randomGroupList] = useRequest(
        () => {
            if (
                data &&
                data?.activityBaseInfo?.groupType === GroupType.random
            ) {
                return queryRandomGroup({
                    courseNo: data?.activityBaseInfo?.courseNo!,
                    courseSeq: data?.activityBaseInfo?.courseSeq!,
                    randomGroupKey: data?.activityBaseInfo?.randomGroupKey,
                    weektimeId: data?.activityBaseInfo?.weektimeId!
                });
            } else {
                return new Promise<GroupListItem[]>(resolve => resolve([]));
            }
        },
        {},
        [data?.activityBaseInfo]
    );

    const joinList = useMemo(() => {
        if (data?.activityBaseInfo?.groupType === GroupType.base) {
            return groupList;
        } else {
            return randomGroupList;
        }
    }, [groupList, data?.activityBaseInfo?.groupType, randomGroupList]);

    return (
        <div className={style.root}>
            <Spin spinning={loading}>
                <div className={style.header}>
                    <div className={style.info}>
                        <div className={style.title}>
                            {data?.activityBaseInfo.activityName}
                        </div>
                        <div className={style.time}>
                            <div className={style.times}>
                                截止时间：{data?.activityBaseInfo.deadlineStr}
                            </div>
                            <div className={style.tip}>
                                <span className={style.mtitle}>参与小组</span>
                                <div className={style.flex}>
                                    {joinList?.map(item => {
                                        if (
                                            data?.activityBaseInfo.groupIds?.includes(
                                                item?.id.toString()
                                            )
                                        ) {
                                            return (
                                                <span
                                                    className={style.groupItem}
                                                    key={item.id}>
                                                    {item.groupName}
                                                </span>
                                            );
                                        }
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={style.container}>
                    <div className={style.list}>
                        <TaskListMenu
                            list={data?.taskList || []}
                            loading={loading}
                            taskId={taskId}
                            onChangeTaskId={setTaskId}
                            preview
                        />
                    </div>
                    <div className={style.answerWrapper}>
                        {taskDetail && (
                            <>
                                <div className={style.groupName}>
                                    参与小组
                                    {taskDetail?.groupNames
                                        ?.split(",")
                                        ?.map(item => (
                                            <span
                                                className={style.groupItem}
                                                key={item}>
                                                {item}
                                            </span>
                                        ))}
                                </div>
                                <div
                                    className={style.answer}
                                    dangerouslySetInnerHTML={{
                                        __html: taskDetail?.taskQuestion
                                            .split("\n")
                                            .join("<br/>")
                                    }}
                                />
                                {!!taskDetail?.taskFileEntityList?.length && (
                                    <>
                                        <div className={style.file}>附件</div>
                                        <div className={style.fileList}>
                                            {taskDetail?.taskFileEntityList.map(
                                                item => (
                                                    <FileList
                                                        key={
                                                            item.key || item.id
                                                        }
                                                        fileName={item.fileName}
                                                        fileSize={
                                                            item?.fileSize
                                                        }
                                                        isPreview
                                                        onPrev={() =>
                                                            onPrev(item.id)
                                                        }
                                                        onDownload={() =>
                                                            handelDownload(
                                                                item.fileName,
                                                                item.fileUrl
                                                            )
                                                        }
                                                    />
                                                )
                                            )}
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {!taskDetail && (
                            <CustomEmpty style={{ margin: "110px 0" }} />
                        )}
                    </div>
                </div>
            </Spin>

            <PreviewFileModal
                visible={prevShow}
                onCancel={togglePrev}
                selectedIndex={defaultIndex}
                list={taskDetail?.taskFileEntityList || []}
                asideRender={taskDetail?.taskFileEntityList?.map(
                    (item, index) => (
                        <AsideLink
                            key={item.id}
                            active={index === defaultIndex}
                            onClick={() => setDefaultIndex(index)}
                            item={item}
                        />
                    )
                )}
            />

            {!preview && (
                <Footer>
                    <Button
                        type="primary"
                        ghost
                        onClick={() => onChangeStep(1)}>
                        上一步
                    </Button>

                    <Button
                        type="primary"
                        onClick={onPublish}
                        loading={submitLoading}>
                        发布
                    </Button>
                </Footer>
            )}
        </div>
    );
}
